import { useEffect, useState } from 'react';

import { usePermission } from '../../../../../auth/AuthContext';
import { Skeleton } from '../../../../../components/Skeleton';
import { todayCalendarDate } from '../../../../../components/calendar/monthGrid';
import { Checkbox } from '../../../../../components/inputs/Checkbox';
import { DateField } from '../../../../../components/inputs/DateField';
import { FieldGroup } from '../../../../../components/inputs/FieldGroup';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../../lib/salutations';
import { Select } from '../../../../../components/inputs/Select';
import { TextField } from '../../../../../components/inputs/TextField';
import { personnelApi, personsApi } from '../../personnelApi';
import {
  CIVIL_STATUS_LABELS,
  SEX_LABELS,
  type Citizenship as CitizenshipValue,
  type CivilStatus,
  type DualCitizenshipKind,
  type PersonnelCatalogs,
  type PersonnelDetail,
  type Sex,
} from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';
import { CitizenshipFields } from './CitizenshipFields';
import { PersonnelSectionModal } from './PersonnelSectionModal';
import { useSectionForm } from './sectionForm';


/**
 * The Personal card's editor.
 *
 * **The name and the date of birth are here too**, at the top, even though they live in
 * `rsys_pii` and everything else on this form lives in `rsys_`. They shipped as a second
 * entry in the card's cog and that was wrong: which database a field is stored in is this
 * system's business, not the reader's, and a card offering "Edit" beside "Edit name and date
 * of birth" made them answer a question about our storage before they could correct a phone
 * number. **One section, one Edit.**
 *
 * **The identity half DEGRADES; it does not widen the gate.** Reading those values is
 * `personnel.reveal`, which most readers correcting a phone number do not hold — so for them
 * the fields are simply absent and `build()` omits them, leaving the identity row untouched.
 * The section still needs `personnel.update` and nothing more, which is the rule: the second
 * key buys the extra fields, not the ability to open the form.
 *
 * **The reveal fires on OPEN, never on page load.** A profile that revealed in order to
 * render would audit an access every time anyone looked at an employee.
 */
export function EditPersonalModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [sex, setSex] = useState<Sex>(1);
  const [civilStatus, setCivilStatus] = useState<CivilStatus | null>(null);
  const [bloodTypeId, setBloodTypeId] = useState<string | null>(null);
  const [nationalityId, setNationalityId] = useState<string | null>(null);
  const [religionId, setReligionId] = useState<string | null>(null);

  const [citizenship, setCitizenship] = useState<CitizenshipValue | null>(null);
  const [dualKind, setDualKind] = useState<DualCitizenshipKind | null>(null);
  const [dualCountryId, setDualCountryId] = useState<string | null>(null);
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isPwd, setIsPwd] = useState(false);
  const [pwdIdNumber, setPwdIdNumber] = useState('');
  const [catalogs, setCatalogs] = useState<PersonnelCatalogs>(NO_CATALOGS);

  const canReveal = usePermission('personnel.reveal');

  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  /*
    Whether the reveal actually landed. It gates BOTH the fields and the request: sending an
    identity block that was never filled in would write empty strings over a real name, and a
    failed reveal is exactly the case where that would otherwise happen silently.
  */
  const [identityLoaded, setIdentityLoaded] = useState(false);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    if (!open || !canReveal) {
      setIdentityLoaded(false);
      return;
    }

    const controller = new AbortController();
    setRevealing(true);

    personsApi
      .reveal(record.personId, controller.signal)
      .then((identity) => {
        if (controller.signal.aborted) return;

        setSalutation(identity.salutation ?? '');
        setFirstName(identity.firstName);
        setMiddleName(identity.middleName ?? '');
        setLastName(identity.lastName);
        setSuffix(identity.suffix ?? '');
        setDateOfBirth(identity.dateOfBirth ?? '');
        setIdentityLoaded(true);
      })
      .catch(() => {
        // The rest of the form still works; the name half simply is not offered.
        if (!controller.signal.aborted) setIdentityLoaded(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setRevealing(false);
      });

    return () => controller.abort();
  }, [open, canReveal, record.personId]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCatalogs(next);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open]);

  function seed() {
    setSex(record.sex);
    setCivilStatus(record.civilStatus ?? null);
    setBloodTypeId(record.bloodTypeId);
    setNationalityId(record.nationalityId);
    setReligionId(record.religionId);
    setCitizenship(record.citizenship ?? null);
    setDualKind(record.dualCitizenshipKind ?? null);
    setDualCountryId(record.dualCitizenshipCountryId ?? null);
    setContactNumber(record.contactNumber ?? '');
    setEmail(record.email ?? '');
    setIsPwd(record.isPwd);
    setPwdIdNumber(record.pwdIdNumber ?? '');
  }

  /*
    Autogenerate on an EDIT form — allowed since 14 Sep 2026. The catalogs are the ones this
    modal already loaded, so every picker resolves to a row that exists.

    **It leaves the name and the date of birth alone, deliberately.** They are required fields
    now, which normally obliges the generator to fill them — but they arrive already filled
    from the reveal, so the primary action still enables on the first click, and overwriting a
    real person's name with sample data is the one thing an edit form must never do. Do not
    "fix" this by adding them.
  */
  function generate() {
    const sample = samplePersonnel({ catalogs, identificationTypes: [] });

    setSex(sample.sex);
    setCivilStatus(sample.civilStatus);
    setCitizenship(sample.citizenship);
    setBloodTypeId(sample.bloodTypeId);
    setNationalityId(sample.nationalityId);
    setReligionId(sample.religionId);
    setContactNumber(sample.contactNumber);
    setEmail(sample.email);

    /*
      The id number is filled BEHIND the PWD flag, which stays where the record left it —
      the create wizard already does exactly this. Flipping it on an edit form would
      overrule what the record says about the person, which is worse than unpredictable:
      it looks like the stored answer.
    */
    setPwdIdNumber(sample.pwdIdNumber);
  }

  function build() {
    return {
      /*
        All three together or none — the server refuses a PARTIAL identity, and a request
        carrying two of the three reads as "blank the one I left out". Spread only when the
        reveal landed, so a reader who never saw these fields never writes them.
      */
      ...(identityLoaded
        ? {
            salutation: salutation.trim() ? salutation.trim() : null,
            firstName: firstName.trim(),
            middleName: middleName.trim() || null,
            lastName: lastName.trim(),
            suffix: suffix.trim() || null,
            dateOfBirth,
          }
        : {}),
      sex,
      civilStatus,
      bloodTypeId,
      nationalityId,
      religionId,
      citizenship,

      /*
        Cleared when the answer is not dual. The server refuses the contradiction anyway, but
        sending it would mean the form knowingly posts something it has been told is wrong —
        and a reader who ticks Dual, picks a country, then goes back to Filipino should not
        see a rejection naming a field the form has just hidden.
      */
      dualCitizenshipKind: citizenship === 2 ? dualKind : null,
      dualCitizenshipCountryId: citizenship === 2 ? dualCountryId : null,

      contactNumber: contactNumber.trim() || null,
      email: email.trim() || null,
      isPwd,
      // A record that does not claim a disability carries no id number; keeping a stale one
      // behind an unticked box is a value nothing on screen accounts for.
      pwdIdNumber: isPwd ? pwdIdNumber.trim() || null : null,
    };
  }

  /*
    Only the identity half has required fields, so a reader without the reveal has nothing
    that can be incomplete — `identityLoaded` false means valid, not blocked.
  */
  const identityValid =
    !identityLoaded ||
    (firstName.trim().length > 0 && lastName.trim().length > 0 && dateOfBirth.length > 0);

  /*
    A dual citizenship is not stated until both follow-ups are answered. Filipino — and a
    record that predates the question — has nothing further to complete.
  */
  const citizenshipValid = citizenship !== 2 || (dualKind !== null && dualCountryId !== null);

  const valid = identityValid && citizenshipValid;

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit personal information"
      idPrefix="personnel-personal"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
      valid={valid}
    >
      <Identity
        canReveal={canReveal}
        revealing={revealing}
        loaded={identityLoaded}
        salutation={salutation}
        setSalutation={setSalutation}
        firstName={firstName}
        setFirstName={setFirstName}
        middleName={middleName}
        setMiddleName={setMiddleName}
        lastName={lastName}
        setLastName={setLastName}
        suffix={suffix}
        setSuffix={setSuffix}
        dateOfBirth={dateOfBirth}
        setDateOfBirth={setDateOfBirth}
      />

      <CitizenshipFieldsBound
        citizenship={citizenship}
        setCitizenship={setCitizenship}
        kind={dualKind}
        setKind={setDualKind}
        countryId={dualCountryId}
        setCountryId={setDualCountryId}
        countries={catalogs.countries}
        countryName={record.dualCitizenshipCountryName ?? null}
      />

      <Fields
        sex={sex}
        setSex={setSex}
        civilStatus={civilStatus}
        setCivilStatus={setCivilStatus}
        bloodTypeId={bloodTypeId}
        setBloodTypeId={setBloodTypeId}
        nationalityId={nationalityId}
        setNationalityId={setNationalityId}
        religionId={religionId}
        setReligionId={setReligionId}
        contactNumber={contactNumber}
        setContactNumber={setContactNumber}
        email={email}
        setEmail={setEmail}
        isPwd={isPwd}
        setIsPwd={setIsPwd}
        pwdIdNumber={pwdIdNumber}
        setPwdIdNumber={setPwdIdNumber}
        catalogs={catalogs}
        record={record}
      />
    </PersonnelSectionModal>
  );
}

/**
 * The identity store's half of this section — the name and the date of birth.
 *
 * It renders nothing at all for a reader who cannot reveal. Not a disabled row, not a row of
 * em dashes: those would state that the record has no name, which is false. The form is simply
 * the rest of the section, and its one Edit still works.
 */
function Identity(props: {
  canReveal: boolean;
  revealing: boolean;
  loaded: boolean;
  salutation: string;
  setSalutation: (next: string) => void;
  firstName: string;
  setFirstName: (next: string) => void;
  middleName: string;
  setMiddleName: (next: string) => void;
  lastName: string;
  setLastName: (next: string) => void;
  suffix: string;
  setSuffix: (next: string) => void;
  dateOfBirth: string;
  setDateOfBirth: (next: string) => void;
}) {
  const { saving, error } = useSectionForm();

  if (!props.canReveal) return null;

  if (props.revealing) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (!props.loaded) {
    return (
      <p className="text-xs text-slate-500">
        The name and date of birth could not be read, so they are not shown. Everything else on this
        form still saves, and they are left exactly as they are.
      </p>
    );
  }

  const firstValid = props.firstName.trim().length > 0;
  const lastValid = props.lastName.trim().length > 0;

  return (
    <>
      <p className="text-xs text-slate-500">
        The name and date of birth are kept in the identity store. Reading them is recorded against
        your account.
      </p>

      {/*
        Three columns — the one exception to the two-column grid. First, middle and last are a
        single fact split across three controls, so they read as one line, and the last column
        is wider because it also carries the suffix segment.
      */}
      <div className="grid gap-4 sm:grid-cols-3">
        <FieldGroup
          label="First name"
          labelFor="personnel-personal-first-name"
          description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
          segments={[
            {
              id: 'personnel-personal-salutation',
              label: 'Salutation',
              value: props.salutation,
              onChange: props.setSalutation,
              options: SALUTATION_OPTIONS,
              allowCustom: true,
              error: error?.fieldError('salutation'),
              disabled: saving,
              width: SALUTATION_SEGMENT_WIDTH,
              emptyLabel: SALUTATION_EMPTY_LABEL,
            },
            {
              id: 'personnel-personal-first-name',
              label: 'First name',
              value: props.firstName,
              onChange: props.setFirstName,
              required: true,
              error: firstValid ? error?.fieldError('firstName') : 'Enter a first name.',
              disabled: saving,
            },
          ]}
        />

        <TextField
          id="personnel-personal-middle-name"
          label="Middle name"
          value={props.middleName}
          onChange={(e) => props.setMiddleName(e.target.value)}
          error={error?.fieldError('middleName')}
          disabled={saving}
        />

        <FieldGroup
          label="Last name"
          description="The narrow box is the suffix — Jr., Sr., III. It is part of the name, not a title."
          segments={[
            {
              id: 'personnel-personal-last-name',
              label: 'Last name',
              value: props.lastName,
              onChange: props.setLastName,
              required: true,
              error: lastValid ? error?.fieldError('lastName') : 'Enter a last name.',
              disabled: saving,
            },
            {
              id: 'personnel-personal-suffix',
              label: 'Suffix',
              value: props.suffix,
              onChange: props.setSuffix,
              error: error?.fieldError('suffix'),
              disabled: saving,
              width: 'w-20 shrink-0',
            },
          ]}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id="personnel-personal-date-of-birth"
          label="Date of birth"
          required
          showAge
          /* Nobody is born tomorrow, and the picker must refuse what the form refuses. */
          max={todayCalendarDate()}
          value={props.dateOfBirth}
          onChange={props.setDateOfBirth}
          error={props.dateOfBirth ? error?.fieldError('dateOfBirth') : 'Choose a date of birth.'}
          disabled={saving}
        />
      </div>
    </>
  );
}

function Fields(props: {
  sex: Sex;
  setSex: (next: Sex) => void;
  civilStatus: CivilStatus | null;
  setCivilStatus: (next: CivilStatus | null) => void;
  bloodTypeId: string | null;
  setBloodTypeId: (next: string | null) => void;
  nationalityId: string | null;
  setNationalityId: (next: string | null) => void;
  religionId: string | null;
  setReligionId: (next: string | null) => void;
  contactNumber: string;
  setContactNumber: (next: string) => void;
  email: string;
  setEmail: (next: string) => void;
  isPwd: boolean;
  setIsPwd: (next: boolean) => void;
  pwdIdNumber: string;
  setPwdIdNumber: (next: string) => void;
  catalogs: PersonnelCatalogs;
  record: PersonnelDetail;
}) {
  const { saving, error } = useSectionForm();

  /*
    The stored value is injected as an option so a picker states its value on the first paint.
    `<Select>` finds its label among its options and the catalog arrives a moment later, so an
    id with no matching option renders as the placeholder — which reads as an empty field on a
    record that has one.
  */
  const optionsFor = (
    entries: { id: string; name: string }[],
    selected: string | null,
    storedName: string | null,
  ) => {
    const options = entries.map((entry) => ({ value: entry.id, label: entry.name }));

    if (selected && !entries.some((entry) => entry.id === selected)) {
      options.unshift({ value: selected, label: storedName ?? '…' });
    }

    return options;
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id="personnel-personal-sex"
          label="Sex"
          required
          description="As recorded on the civil registry document."
          value={String(props.sex)}
          onChange={(next) => props.setSex(Number(next) as Sex)}
          options={Object.entries(SEX_LABELS).map(([value, label]) => ({ value, label }))}
          sort="none"
          clearable={false}
          error={error?.fieldError('sex')}
          disabled={saving}
        />

        <Select
          id="personnel-personal-civil-status"
          label="Civil status"
          value={props.civilStatus === null ? '' : String(props.civilStatus)}
          onChange={(next) => props.setCivilStatus(next ? (Number(next) as CivilStatus) : null)}
          options={Object.entries(CIVIL_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          sort="none"
          error={error?.fieldError('civilStatus')}
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Select
          id="personnel-personal-blood-type"
          label="Blood type"
          value={props.bloodTypeId ?? ''}
          onChange={(next) => props.setBloodTypeId(next || null)}
          options={optionsFor(
            props.catalogs.bloodTypes,
            props.bloodTypeId,
            props.record.bloodTypeName,
          )}
          error={error?.fieldError('bloodTypeId')}
          disabled={saving}
        />

        <Select
          id="personnel-personal-nationality"
          label="Nationality"
          value={props.nationalityId ?? ''}
          onChange={(next) => props.setNationalityId(next || null)}
          options={optionsFor(
            props.catalogs.nationalities,
            props.nationalityId,
            props.record.nationalityName,
          )}
          error={error?.fieldError('nationalityId')}
          disabled={saving}
        />

        <Select
          id="personnel-personal-religion"
          label="Religion"
          value={props.religionId ?? ''}
          onChange={(next) => props.setReligionId(next || null)}
          options={optionsFor(
            props.catalogs.religions,
            props.religionId,
            props.record.religionName,
          )}
          error={error?.fieldError('religionId')}
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="personnel-personal-contact-number"
          label="Contact number"
          value={props.contactNumber}
          onChange={(e) => props.setContactNumber(e.target.value)}
          error={error?.fieldError('contactNumber')}
          disabled={saving}
        />

        <TextField
          id="personnel-personal-email"
          label="Email"
          type="email"
          value={props.email}
          onChange={(e) => props.setEmail(e.target.value)}
          error={error?.fieldError('email')}
          disabled={saving}
        />
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <label
          htmlFor="personnel-personal-is-pwd"
          className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
        >
          <Checkbox
            id="personnel-personal-is-pwd"
            name="personnel-personal-is-pwd"
            checked={props.isPwd}
            onChange={(e) => props.setIsPwd(e.target.checked)}
            disabled={saving}
          />
          <span>Person with disability</span>
        </label>

        {/* The id number only exists for a record that claims one — offered when the box is
            ticked rather than sitting greyed out asking a question that does not apply. */}
        {props.isPwd ? (
          <TextField
            id="personnel-personal-pwd-id-number"
            label="PWD ID number"
            value={props.pwdIdNumber}
            onChange={(e) => props.setPwdIdNumber(e.target.value)}
            error={error?.fieldError('pwdIdNumber')}
            disabled={saving}
          />
        ) : null}
      </div>
    </>
  );
}

/** `<CitizenshipFields>` with `saving` and `error` taken from the section-form context. */
function CitizenshipFieldsBound(
  props: Omit<Parameters<typeof CitizenshipFields>[0], 'saving' | 'error'>,
) {
  const { saving, error } = useSectionForm();

  return <CitizenshipFields {...props} saving={saving} error={error} />;
}
